import React, { useRef } from 'react';

import Clickable from 'ds/Clickable';
import Icon from 'ds/Icon';
import Layout from 'ds/Layout';
import MessageImageUploadThumbnail from 'ds/MessageThread/MessageImageUploadThumbnail';
import MessagePdfUploadThumbnail from 'ds/MessageThread/MessagePdfUploadThumbnail';
import Text, { Size } from 'ds/Text';
import { isImageFile, normalizeImageFilesForUpload } from 'helpers/images';
import { EditableImage, EditablePdf, IconSize } from 'shared';

import IconButton from './IconButton';

const IMAGE_AND_PDF_ACCEPT =
  'image/png,image/jpeg,image/jpg,image/webp,image/heic,image/heif,.heic,.heif,application/pdf,.pdf';

interface Props {
  images?: EditableImage[];
  pdfs?: EditablePdf[];
  onImagesChange: (files: File[]) => void;
  onPdfsChange: (files: File[]) => void;
  onImageDelete?: (image: EditableImage) => void;
  onPdfDelete?: (pdf: EditablePdf) => void;
  disabled?: boolean;
  maxImages?: number;
  maxPdfs?: number;
  label?: string;
  labelSize?: Size;
  imageDimension?: number;
  pdfDimension?: { width: number; height: number };
  iconSize?: IconSize;
  showAttachments?: boolean;
}

const AttachmentUploader: React.FC<Props> = ({
  images = [],
  pdfs = [],
  onImagesChange,
  onPdfsChange,
  onImageDelete,
  onPdfDelete,
  disabled,
  maxImages = 12,
  maxPdfs = 5,
  label,
  labelSize = 'body-xs',
  imageDimension = 98,
  pdfDimension = { width: 272, height: 48 },
  iconSize = 'xs',
  showAttachments = true
}) => {
  const hiddenFileInputRef = useRef<HTMLInputElement>(null);
  const showFileChooser = () => hiddenFileInputRef.current?.click();

  const activeImages = images.filter(i => !i._destroy);
  const activePdfs = pdfs.filter(p => !p._destroy);
  const gap = 6;

  return (
    <Layout direction="column" gap={24}>
      {/* Add attachments button - always visible */}
      {label ? (
        <Clickable
          onClick={showFileChooser}
          style={{
            display: 'flex',
            alignItems: 'center',
            paddingLeft: 8,
            paddingRight: 8,
            paddingTop: 10,
            paddingBottom: 10
          }}
          disabled={disabled}
        >
          <Icon name="plus" size={iconSize} stroke={2} />
          {label && (
            <Layout marginLeft={4}>
              <Text size={labelSize}>{label}</Text>
            </Layout>
          )}
        </Clickable>
      ) : (
        <IconButton
          name="plus"
          size="md"
          type="noBackground"
          onClick={showFileChooser}
          disabled={disabled}
          stroke={2}
          iconSize={'md'}
        />
      )}

      {/* Images */}
      {activeImages.length > 0 && showAttachments && (
        <Layout flex wrap position="relative" left={-gap} width="calc(100% + 12px)">
          {activeImages.map(image => (
            <MessageImageUploadThumbnail
              key={image.id || image.url}
              containerProps={{
                margin: gap,
                width: imageDimension,
                height: imageDimension
              }}
              disabled={disabled}
              image={image}
              onDeleteClick={onImageDelete ? () => onImageDelete(image) : undefined}
            />
          ))}
        </Layout>
      )}

      {/* PDFs */}
      {activePdfs.length > 0 && showAttachments && (
        <Layout flex wrap position="relative" left={-gap} width="calc(100% + 12px)">
          {activePdfs.map(pdf => (
            <MessagePdfUploadThumbnail
              key={pdf.id || pdf.url || pdf.tempId}
              containerProps={{
                margin: gap,
                width: pdfDimension.width,
                height: pdfDimension.height
              }}
              disabled={disabled}
              pdf={pdf}
              onDeleteClick={onPdfDelete ? () => onPdfDelete(pdf) : undefined}
            />
          ))}
        </Layout>
      )}

      {/* Hidden file input */}
      <input
        ref={hiddenFileInputRef}
        type="file"
        style={{ display: 'none' }}
        accept={IMAGE_AND_PDF_ACCEPT}
        multiple
        onChange={async event => {
          const files = event.target.files;
          if (!files) return;

          const filesArray = Array.from(files);

          // Separate images from PDFs (include HEIC/HEIF by extension when mime is empty)
          const imageFiles = filesArray.filter(isImageFile);
          const pdfFiles = filesArray.filter(
            file => file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
          );

          // Only add up to max allowed for each type
          if (imageFiles.length > 0) {
            const normalizedImages = await normalizeImageFilesForUpload(
              imageFiles.slice(0, maxImages - activeImages.length)
            );
            onImagesChange(normalizedImages);
          }
          if (pdfFiles.length > 0) {
            onPdfsChange(pdfFiles.slice(0, maxPdfs - activePdfs.length));
          }

          event.target.value = '';
        }}
      />
    </Layout>
  );
};

export default AttachmentUploader;
